<template>
    <div>
        <side-sheet
            ref="orderDetailSheet"
            :size="7"
            label="Order detail pagina"
            description="Hier kun je de details inzien van de order"
        >
            <template #default>
                <grid-container template="side-sheet" :color="Color.gray200">
                    <grid-area area="side-sheet-header">
                        <order-detail-header :order="order as Order" />
                    </grid-area>

                    <grid-area area="side-sheet-content" style="overflow-y: hidden;">
                        <grid-container template="two-columns-sidebar">
                            <order-detail-left :order="order as Order" :lead="lead as Lead" />

                            <!--
                                We reuse lead right area
                                because everything in this area gets loaded from lead anyway
                            -->
                            <lead-detail-right-skeleton>
                                <lead-detail-right :lead="lead as Lead" />
                            </lead-detail-right-skeleton>
                        </grid-container>
                    </grid-area>
                </grid-container>

                <router-view :key="route.name ?? 'router-view'" />
            </template>
        </side-sheet>
    </div>
</template>

<script setup lang="ts">
    import {
        Color,
        GridContainer,
        SideSheet,
        GridArea,
    } from '@keyvue/one-ui';

    import OrderDetailHeader from '@views/Order/Detail/OrderDetailHeader.vue';

    import {
        Lead,
    } from '@keyvue/one-api-client';

    import {
        OrderAPI,
        Order,
    } from '@keyvue/one-api-client-legacy';

    import { computed, provide, ref } from 'vue';
    import { useRoute, useRouter } from 'vue-router';

    import useClient from '@/composables/useClient';
    import useOverlayAsRoute from '@composables/useOverlayAsRoute';
    import LeadDetailRightSkeleton from '@components/Skeleton/Area/LeadDetailRightSkeleton.vue';
    import LeadDetailRight from '@views/Lead/Detail/Areas/LeadDetailRight.vue';
    import OrderDetailLeft from '@views/Order/Detail/Areas/OrderDetailLeft.vue';
    import useEventBus from '@composables/useEventBus';

    interface Props {
        orderId: string;
    }

    const props = defineProps<Props>();
    const { client } = useClient();
    const eventBus = useEventBus();
    const route = useRoute();
    const router = useRouter();

    const orderDetailSheet = ref<any>();

    const parentRoutePrefix = computed(() => (router.currentRoute.value?.name?.toString() ?? '')
        .replace(/OrderDetail/g, ''));
    const parentRouteParams = Object.entries(router.currentRoute.value?.params || {})
        .filter(([key]) => key !== 'orderId')
        .reduce((paramsObject, [key, value]) => ({ ...paramsObject, [key]: value }), {});

    provide('parentRoutePrefix', parentRoutePrefix);
    provide('parentRouteParams', parentRouteParams);

    const order = ref<Order>(await OrderAPI.get(props.orderId));
    const lead = ref<Lead>(await client.get(Lead, order.value.referral.id));

    eventBus.subscribe<string>('resource-manipulated', async (params) => {
        if (params === 'Order') {
            order.value = await OrderAPI.get(props.orderId);
            lead.value = await client.get(Lead, order.value.referral.id);
        }
    });

    const closeOrder = () => {
        router.push({
            name: 'contactDetail',
            params: {
                contactId: route.params.contactId,
            },
        });
    };

    provide('closeOrder', closeOrder);

    useOverlayAsRoute({
        overlay: orderDetailSheet,
        isSideSheet: true,
        redirectMethod: closeOrder,
    });
</script>

<style scoped lang="scss">
.order-detail__sidebar {
    background-color: color(brand-white);

    &__tab-labels {
        padding-top: size(sm);
        width: 100%;
        display: flex;
        justify-content: center;
    }

    &__tab-label {
        display: flex;
        align-items: center;
        gap: size(sm);
    }
}
</style>
